import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip,
  TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Alert, Stack, Avatar, Divider
} from '@mui/material';
import {
  ArrowBack, Edit, Delete, CloudUpload, Visibility, Badge as BadgeIcon, Person, QrCode, Download
} from '@mui/icons-material';
import { staffAPI } from '../services/api';
import { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import html2canvas from 'html2canvas';

const STATUS_OPTIONS = ['active', 'inactive', 'on_leave', 'terminated', 'resigned'];
const EMPLOYMENT_TYPE_OPTIONS = ['full_time', 'part_time', 'contract', 'intern'];
const DEPARTMENT_OPTIONS = ['Sales', 'Inventory', 'Finance', 'Management', 'IT', 'HR', 'Operations'];
const BRANCH_OPTIONS = ['Headquarters', 'Bole', 'Megenagna', 'Kazanchis', 'CMC', 'Piassa', 'Other'];
const GENDER_OPTIONS = ['male', 'female', 'other'];
const DOCUMENT_TYPES = ['profile_photo', 'national_id', 'passport', 'work_id', 'guarantor_form', 'contract', 'cv', 'certificate', 'other'];

const StaffIdCard = ({ open, onClose, staff }) => {
  const [cardSide, setCardSide] = useState('front');

  const handleDownload = async () => {
    try {
      const frontElement = document.getElementById('staff-id-card-front');
      const backElement = document.getElementById('staff-id-card-back');
      
      // Store original display values
      const originalFrontDisplay = frontElement.style.display;
      const originalBackDisplay = backElement.style.display;
      
      // Preload and convert staff profile photo to base64 to avoid CORS issues
      const imgElement = frontElement?.querySelector('#staff-profile-photo');
      if (imgElement && staff.profile_photo_url) {
        const response = await fetch(staff.profile_photo_url);
        const blob = await response.blob();
        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = () => {
            imgElement.src = reader.result;
            resolve();
          };
          reader.readAsDataURL(blob);
        });
      }

      // Temporarily make both sides visible for capture
      frontElement.style.display = 'block';
      backElement.style.display = 'block';

      // Capture front side
      const frontCanvas = await html2canvas(frontElement, {
        scale: 4,
        useCORS: true,
        backgroundColor: null,
        allowTaint: true,
        logging: false,
        imageTimeout: 5000,
      });

      // Capture back side
      const backCanvas = await html2canvas(backElement, {
        scale: 4,
        useCORS: true,
        backgroundColor: null,
        allowTaint: true,
        logging: false,
        imageTimeout: 5000,
      });

      // Restore original display values
      frontElement.style.display = originalFrontDisplay;
      backElement.style.display = originalBackDisplay;

      // Download front
      const frontLink = document.createElement('a');
      frontLink.download = `${staff.first_name}_${staff.last_name}_id_card_front.png`;
      frontLink.href = frontCanvas.toDataURL('image/png', 1.0);
      frontLink.click();

      // Download back with slight delay
      setTimeout(() => {
        const backLink = document.createElement('a');
        backLink.download = `${staff.first_name}_${staff.last_name}_id_card_back.png`;
        backLink.href = backCanvas.toDataURL('image/png', 1.0);
        backLink.click();
      }, 500);

      toast.success('ID Card downloaded successfully (front and back)');
    } catch (error) {
      toast.error('Failed to download ID Card');
      console.error('Download error:', error);
    }
  };

  if (!staff) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Staff ID Card
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => setCardSide(cardSide === 'front' ? 'back' : 'front')} color="primary">
            {cardSide === 'front' ? <ArrowBack /> : <ArrowBack sx={{ transform: 'rotate(180deg)' }} />}
          </IconButton>
          <IconButton onClick={handleDownload} color="primary">
            <Download />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          id="staff-id-card-front"
          sx={{
            display: cardSide === 'front' ? 'block' : 'none',
            background: 'linear-gradient(180deg, #1a365d 0%, #2c5282 100%)',
            borderRadius: 2,
            p: 3,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            aspectRatio: '1.58 / 1',
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  background: 'white',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <img 
                  src="/logo.png" 
                  alt="Logo" 
                  style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span style="font-size: 20px; font-weight: 900; color: #1a365d;">DDIG</span>';
                  }}
                />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
                  DDIG Trading PLC
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Employee ID Card
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                Issued
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                {staff.hire_date ? new Date(staff.hire_date).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </Box>

          {/* Separator Line */}
          <Box sx={{ height: '2px', background: 'rgba(255,255,255,0.3)', mb: 2 }} />

          {/* Main Content */}
          <Box sx={{ display: 'flex', gap: 2.5, mb: 1.5 }}>
            {/* Photo */}
            <Box sx={{ flex: 0 }}>
              <Box
                sx={{
                  width: 125,
                  height: 130,
                  border: '3px solid white',
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.1)',
                }}
              >
                {staff.profile_photo_url ? (
                  <img
                    id="staff-profile-photo"
                    src={staff.profile_photo_url}
                    alt={`${staff.first_name} ${staff.last_name}`}
                    crossOrigin="anonymous"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Person sx={{ fontSize: 52 }} />
                )}
              </Box>
              <Box
                sx={{
                  mt: 0.75,
                  textAlign: 'center',
                  background: staff.status === 'active' ? '#48bb78' : '#ed8936',
                  color: 'white',
                  py: 0.4,
                  px: 1.25,
                  borderRadius: 0.5,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
              >
                {staff.status}
              </Box>
            </Box>

            {/* Details Box */}
            <Box sx={{ 
              flex: 1,
              background: 'rgba(255,255,255,0.1)',
              borderRadius: 1.5,
              p: 1.25,
              border: '1px solid rgba(255,255,255,0.2)',
            }}>
              <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.1rem', mb: 0.2 }}>
                {staff.first_name} {staff.middle_name} {staff.last_name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontSize: '0.8rem' }}>
                {staff.position}
              </Typography>

              <Box sx={{ mb: 0.75 }}>
                <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.55rem', textTransform: 'uppercase' }}>
                  Employee ID
                </Typography>
                <Typography variant="body1" fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  {staff.company_id}
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.55rem', textTransform: 'uppercase' }}>
                    Department
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                    {staff.department}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.55rem', textTransform: 'uppercase' }}>
                    Branch
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                    {staff.branch || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.55rem', textTransform: 'uppercase' }}>
                    Phone
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                    {staff.phone || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.55rem', textTransform: 'uppercase' }}>
                    Email
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                    {staff.email || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.2)', pt: 1.5 }}>
            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.6rem' }}>
              Valid Until: {staff.contract_end_date || 'Indefinite'}
            </Typography>
            <Box
              sx={{
                width: 45,
                height: 45,
                background: 'white',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <QrCode sx={{ fontSize: 32, color: '#1a365d' }} />
            </Box>
          </Box>
        </Box>
        <Box
          id="staff-id-card-back"
          sx={{
            display: cardSide === 'back' ? 'block' : 'none',
            background: 'linear-gradient(180deg, #1a365d 0%, #2c5282 100%)',
            borderRadius: 2,
            p: 3,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            aspectRatio: '1.58 / 1',
            maxWidth: 600,
            mx: 'auto',
          }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    background: 'white',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <img 
                    src="/logo.png" 
                    alt="Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<span style="font-size: 20px; font-weight: 900; color: #1a365d;">DDIG</span>';
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
                    DDIG Trading PLC
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    Employee ID Card
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                  Issued
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                  {staff.hire_date ? new Date(staff.hire_date).toLocaleDateString() : 'N/A'}
                </Typography>
              </Box>
            </Box>

            {/* Separator Line */}
            <Box sx={{ height: '2px', background: 'rgba(255,255,255,0.3)', mb: 2 }} />

            {/* Terms and Conditions */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: '0.85rem', mb: 0.2 }}>
                Terms and Conditions
              </Typography>
              <Box sx={{ 
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 1.5,
                p: 1,
                border: '1px solid rgba(255,255,255,0.2)',
                flex: 1,
              }}>
                <Typography variant="body2" sx={{ fontSize: '0.65rem', lineHeight: 1.4, opacity: 0.9 }}>
                  1. This ID card is the property of DDIG Trading PLC and must be surrendered upon termination of employment.
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.65rem', lineHeight: 1.4, opacity: 0.9, mt: 0.5 }}>
                  2. The cardholder must report loss or damage of this card immediately to HR department.
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.65rem', lineHeight: 1.4, opacity: 0.9, mt: 0.5 }}>
                  3. This card is non-transferable and must be carried at all times while on company premises.
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.65rem', lineHeight: 1.4, opacity: 0.9, mt: 0.5 }}>
                  4. Misuse of this card may result in disciplinary action.
                </Typography>
              </Box>

              {/* Emergency Contact */}
              <Box sx={{ 
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 1.5,
                p: 1,
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.55rem', textTransform: 'uppercase', mb: 0.2, display: 'block' }}>
                  Emergency Contact
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                  {staff.emergency_contact_name || 'N/A'}: {staff.emergency_contact_phone || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<Download />} onClick={handleDownload}>
          Download ID Card
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DocumentDialog = ({ open, onClose, staffId, onUploaded }) => {
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      document_type: '',
      document_name: '',
      description: '',
      is_primary: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const onSubmit = async (data) => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', data.document_type);
      if (data.document_name) formData.append('document_name', data.document_name);
      if (data.description) formData.append('description', data.description);
      formData.append('is_primary', data.is_primary);

      await staffAPI.uploadDocument(staffId, formData);
      toast.success('Document uploaded successfully');
      reset();
      setFile(null);
      onUploaded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Upload Document</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Document Type *</InputLabel>
                <Select {...register('document_type', { required: 'Document type required' })} label="Document Type" defaultValue="">
                  <MenuItem value="">Select type</MenuItem>
                  {DOCUMENT_TYPES.map(type => (
                    <MenuItem key={type} value={type}>{type.replace('_', ' ').toUpperCase()}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Document Name" {...register('document_name')} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                {file ? file.name : 'Select File *'}
                <input type="file" hidden onChange={(e) => setFile(e.target.files[0])} accept="image/*,.pdf" />
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={2} {...register('description')} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Set as Primary</InputLabel>
                <Select {...register('is_primary')} label="Set as Primary" defaultValue={false}>
                  <MenuItem value={false}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<CloudUpload />} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const StaffDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [idCardOpen, setIdCardOpen] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const { register, handleSubmit, reset, watch, setValue, control } = useForm({
  defaultValues: {
    department: '',
    branch: '',
    employment_type: '',
    gender: '',
    status: 'active',
    first_name: '',
    last_name: '',
    middle_name: '',
    email: '',
    phone: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    position: '',
    date_of_birth: '',
    hire_date: '',
    probation_end_date: '',
    contract_end_date: '',
    termination_date: '',
    salary: '',
    account_number: '',
    tin: '',
  }
});

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await staffAPI.getById(id);
      const staffData = data.data;
      
      // Format dates for date inputs (YYYY-MM-DD)
      const formattedData = {
        ...staffData,
        date_of_birth: staffData.date_of_birth ? staffData.date_of_birth.split('T')[0] : '',
        hire_date: staffData.hire_date ? staffData.hire_date.split('T')[0] : '',
        probation_end_date: staffData.probation_end_date ? staffData.probation_end_date.split('T')[0] : '',
        contract_end_date: staffData.contract_end_date ? staffData.contract_end_date.split('T')[0] : '',
        termination_date: staffData.termination_date ? staffData.termination_date.split('T')[0] : '',
      };
      
      setStaff(staffData);
      reset(formattedData);
    } catch {
      toast.error('Failed to load staff member');
    } finally {
      setLoading(false);
    }
  }, [id, reset]);

  useEffect(() => {
    if (id === 'new') {
      setStaff(null);
      setEditMode(true);
      setLoading(false);
    } else {
      fetchStaff();
    }
  }, [id, fetchStaff]);

  const handleGenerateCompanyId = async () => {
    try {
      const { data } = await staffAPI.generateCompanyId();
      setValue('company_id', data.data.company_id);
      toast.success('Company ID generated');
    } catch {
      toast.error('Failed to generate company ID');
    }
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhoto(file);
      setProfilePhotoPreview(URL.createObjectURL(file));
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      
      // First create/update staff
      if (id === 'new') {
        const response = await staffAPI.create(data);
        const newStaffId = response.data.data.id;
        
        // If profile photo was selected, upload it
        if (profilePhoto) {
          const formData = new FormData();
          formData.append('file', profilePhoto);
          formData.append('document_type', 'profile_photo');
          formData.append('document_name', 'Profile Photo');
          formData.append('is_primary', true);
          await staffAPI.uploadDocument(newStaffId, formData);
        }
        
        toast.success('Staff member created successfully');
        navigate('/staffs');
      } else {
        await staffAPI.update(id, data);
        
        // If profile photo was selected, upload it
        if (profilePhoto) {
          const formData = new FormData();
          formData.append('file', profilePhoto);
          formData.append('document_type', 'profile_photo');
          formData.append('document_name', 'Profile Photo');
          formData.append('is_primary', true);
          await staffAPI.uploadDocument(id, formData);
        }
        
        toast.success('Staff member updated successfully');
        setEditMode(false);
        setProfilePhoto(null);
        setProfilePhotoPreview(null);
        fetchStaff();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save staff member');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    try {
      await staffAPI.delete(id);
      toast.success('Staff member deleted successfully');
      navigate('/staffs');
    } catch {
      toast.error('Failed to delete staff member');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await staffAPI.deleteDocument(id, documentId);
      toast.success('Document deleted successfully');
      fetchStaff();
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const handleSetPrimaryDocument = async (documentId) => {
    try {
      await staffAPI.setPrimaryDocument(id, documentId);
      toast.success('Document set as primary');
      fetchStaff();
    } catch {
      toast.error('Failed to set primary document');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (id === 'new' || editMode) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={() => id === 'new' ? navigate('/staffs') : setEditMode(false)}>
            Back
          </Button>
          <Typography variant="h4" fontWeight={700}>
            {id === 'new' ? 'Add New Staff' : 'Edit Staff'}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Profile Photo</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Avatar
                  src={profilePhotoPreview || (staff?.profile_photo_url)}
                  sx={{ width: 100, height: 100, border: '2px solid', borderColor: 'divider' }}
                >
                  {!profilePhotoPreview && !staff?.profile_photo_url && <Person sx={{ fontSize: 50 }} />}
                </Avatar>
              </Grid>
              <Grid item xs={12} md={9}>
                <Button variant="outlined" component="label" fullWidth>
                  {profilePhoto ? profilePhoto.name : 'Select Profile Photo'}
                  <input type="file" hidden accept="image/*" onChange={handleProfilePhotoChange} />
                </Button>
                {profilePhoto && (
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => { setProfilePhoto(null); setProfilePhotoPreview(null); }}
                    sx={{ mt: 1 }}
                  >
                    Remove
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Company Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company ID"
                  {...register('company_id')}
                  InputProps={{
                    endAdornment: (
                      <Button size="small" onClick={handleGenerateCompanyId} disabled={!!watch('company_id')}>
                        Generate
                      </Button>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="department"
                  control={control}
                  rules={{ required: 'Department required' }}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Department *</InputLabel>
                      <Select {...field} label="Department *">
                        <MenuItem value="">Select department</MenuItem>
                        {DEPARTMENT_OPTIONS.map(dept => <MenuItem key={dept} value={dept}>{dept}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="branch"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Branch</InputLabel>
                      <Select {...field} label="Branch">
                        <MenuItem value="">Select branch</MenuItem>
                        {BRANCH_OPTIONS.map(branch => <MenuItem key={branch} value={branch}>{branch}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Position *" {...register('position', { required: 'Position required' })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="employment_type"
                  control={control}
                  rules={{ required: 'Employment type required' }}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Employment Type *</InputLabel>
                      <Select {...field} label="Employment Type *">
                        {EMPLOYMENT_TYPE_OPTIONS.map(type => (
                          <MenuItem key={type} value={type}>{type.replace('_', ' ').toUpperCase()}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Personal Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="First Name *" {...register('first_name', { required: 'First name required' })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Last Name *" {...register('last_name', { required: 'Last name required' })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Middle Name" {...register('middle_name')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Email" type="email" {...register('email')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Phone *" {...register('phone', { required: 'Phone required' })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="date_of_birth"
                  control={control}
                  render={({ field }) => (
                    <TextField fullWidth label="Date of Birth" type="date" InputLabelProps={{ shrink: true }} {...field} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Gender</InputLabel>
                      <Select {...field} label="Gender">
                        <MenuItem value="">Select gender</MenuItem>
                        {GENDER_OPTIONS.map(gender => (
                          <MenuItem key={gender} value={gender}>{gender.charAt(0).toUpperCase() + gender.slice(1)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="National ID/Passport" {...register('national_id')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Emergency Contact Name" {...register('emergency_contact_name')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Emergency Contact Phone" {...register('emergency_contact_phone')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Address" multiline rows={2} {...register('address')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="City" {...register('city')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Country" {...register('country')} defaultValue="Ethiopia" />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Employment Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Salary *" type="number" {...register('salary', { required: 'Salary required' })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Salary Currency" {...register('salary_currency')} defaultValue="ETB" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Account Number" {...register('account_number')} placeholder="Bank account number" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="hire_date"
                  control={control}
                  rules={{ required: 'Hire date required' }}
                  render={({ field }) => (
                    <TextField fullWidth label="Hire Date *" type="date" InputLabelProps={{ shrink: true }} {...field} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="probation_end_date"
                  control={control}
                  render={({ field }) => (
                    <TextField fullWidth label="Probation End Date" type="date" InputLabelProps={{ shrink: true }} {...field} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="contract_end_date"
                  control={control}
                  render={({ field }) => (
                    <TextField fullWidth label="Contract End Date" type="date" InputLabelProps={{ shrink: true }} {...field} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="status"
                  control={control}
                  rules={{ required: 'Status required' }}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Status *</InputLabel>
                      <Select {...field} label="Status *">
                        {STATUS_OPTIONS.map(status => (
                          <MenuItem key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              {watch('status') === 'terminated' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Termination Date" type="date" InputLabelProps={{ shrink: true }} {...register('termination_date')} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Termination Reason" multiline rows={2} {...register('termination_reason')} />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Notes</Typography>
            <TextField fullWidth multiline rows={4} {...register('notes')} />
          </Paper>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => id === 'new' ? navigate('/staffs') : setEditMode(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : (id === 'new' ? 'Create Staff' : 'Save Changes')}
            </Button>
          </Box>
        </form>
      </Box>
    );
  }

  if (!staff) {
    return <Alert severity="error">Staff member not found</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/staffs')}>
          Back
        </Button>
        <Typography variant="h4" fontWeight={700}>
          Staff Profile
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditMode(true)}>
            Edit
          </Button>
          <Button variant="outlined" color="error" startIcon={<Delete />} onClick={handleDelete}>
            Delete
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Card - Left Side */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 24 }}>
            <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Avatar
                src={staff.profile_photo_url ? staff.profile_photo_url : undefined}
                sx={{ width: 320, height: 320, border: '4px solid', borderColor: 'primary.main', mb: 3 }}
              >
                {!staff.profile_photo_url && <Person sx={{ fontSize: 160 }} />}
              </Avatar>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {staff.first_name} {staff.last_name}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {staff.position}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontFamily: 'monospace' }}>
                {staff.company_id}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                <StatusChip status={staff.status} />
                <Chip label={staff.branch || 'No Branch'} size="small" variant="outlined" />
                <Chip label={staff.department} size="small" color="primary" variant="outlined" />
              </Box>
              <Button
                variant="contained"
                startIcon={<BadgeIcon />}
                onClick={() => setIdCardOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  },
                }}
              >
                Generate Staff ID
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* Details Cards - Right Side */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {/* Personal Information */}
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
                    <BadgeIcon color="primary" />
                    Personal
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Full Name
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                        {staff.first_name} {staff.middle_name} {staff.last_name}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {staff.email || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Phone
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                        {staff.phone}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Date of Birth
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {staff.date_of_birth ? new Date(staff.date_of_birth).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Gender
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {staff.gender ? staff.gender.charAt(0).toUpperCase() + staff.gender.slice(1) : 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        National ID
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {staff.national_id || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Address
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {staff.address || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        City
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {staff.city || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Country
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {staff.country || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Employment Details */}
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
                    <BadgeIcon color="primary" />
                    Employment
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Department
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                        {staff.department}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Branch
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                        {staff.branch || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Position
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                        {staff.position}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Employment Type
                      </Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip label={staff.employment_type?.replace('_', ' ')} size="small" />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Salary
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                        {staff.salary} {staff.salary_currency}
                      </Typography>
                    </Box>
                    {staff.account_number && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                          Account Number
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {staff.account_number}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <StatusChip status={staff.status} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Hire Date
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {new Date(staff.hire_date).toLocaleDateString()}
                      </Typography>
                    </Box>
                    {staff.probation_end_date && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                          Probation End
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {new Date(staff.probation_end_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                    {staff.contract_end_date && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                          Contract End
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {new Date(staff.contract_end_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Emergency Contact */}
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
                    <BadgeIcon color="primary" />
                    Emergency Contact
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Contact Name
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                        {staff.emergency_contact_name || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                        Contact Phone
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                        {staff.emergency_contact_phone || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Documents */}
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
                      <BadgeIcon color="primary" />
                      Documents
                    </Typography>
                    <Button size="small" variant="outlined" startIcon={<CloudUpload />} onClick={() => setDocumentOpen(true)}>
                      Upload
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {staff.documents && staff.documents.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 1 }}>Type</TableCell>
                          <TableCell sx={{ py: 1 }}>Primary</TableCell>
                          <TableCell sx={{ py: 1 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {staff.documents.map((doc) => (
                          <TableRow key={doc.id} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell sx={{ py: 1 }}>
                              <Chip label={doc.document_type.replace('_', ' ')} size="small" />
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>{doc.is_primary ? 'Yes' : 'No'}</TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <IconButton size="small" onClick={() => window.open(`http://localhost:5000${doc.file_url}`, '_blank')}>
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeleteDocument(doc.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                              {!doc.is_primary && (
                                <IconButton size="small" onClick={() => handleSetPrimaryDocument(doc.id)}>
                                  <BadgeIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No documents uploaded
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {staff.notes && (
              <Grid item xs={12}>
                <Card>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: '1rem' }}>Notes</Typography>
                    <Typography variant="body2">{staff.notes}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <DocumentDialog
        open={documentOpen}
        onClose={() => setDocumentOpen(false)}
        staffId={id}
        onUploaded={fetchStaff}
      />
      <StaffIdCard
        open={idCardOpen}
        onClose={() => setIdCardOpen(false)}
        staff={staff}
      />
    </Box>
  );
};

export default StaffDetailPage;
